import Component from '@averyano/core/src/classes/Component';
import each from 'lodash/each';

export default class PricingSection extends Component {
	constructor() {
		super({
			element: '.pricing',
			elements: {
				currency: '.pricing__currency',
				toggle: '.pricing__currency-toggle',
				current: '.pricing__currency-current',
				options: '.pricing__currency-option',
				amounts: '.pricing__plan-amount',
				codes: '.pricing__plan-code',
				grid: '.pricing__grid',
				cols: '.pricing__col',
				arrowPrev: '.pricing__scroll-arrow--prev',
				arrowNext: '.pricing__scroll-arrow--next',
			},
		});
	}

	create() {
		super.createComponent();
		this.isOpen = false;
		this.setCurrency('dkk');
		this.updateArrows();
	}

	currentIndex() {
		const { grid, cols } = this.elements;
		const viewCenter = grid.scrollLeft + grid.clientWidth / 2;
		let current = 0;
		let min = Infinity;
		each(cols, (col, i) => {
			const colCenter = col.offsetLeft + col.offsetWidth / 2;
			const d = Math.abs(colCenter - viewCenter);
			if (d < min) {
				min = d;
				current = i;
			}
		});
		return current;
	}

	scrollTo(index) {
		const { grid, cols } = this.elements;
		if (!grid || !cols || !cols.length) return;
		const clamped = Math.max(0, Math.min(index, cols.length - 1));
		const col = cols[clamped];
		const left = col.offsetLeft + col.offsetWidth / 2 - grid.clientWidth / 2;
		grid.scrollTo({ left, behavior: 'smooth' });
	}

	updateArrows() {
		const { cols, arrowPrev, arrowNext } = this.elements;
		if (!cols || !cols.length) return;
		const i = this.currentIndex();
		arrowPrev?.classList.toggle('is-hidden', i <= 0);
		arrowNext?.classList.toggle('is-hidden', i >= cols.length - 1);
	}

	open() {
		this.isOpen = true;
		this.elements.currency.classList.add('is-open');
		this.elements.toggle.setAttribute('aria-expanded', 'true');
	}

	close() {
		this.isOpen = false;
		this.elements.currency.classList.remove('is-open');
		this.elements.toggle.setAttribute('aria-expanded', 'false');
	}

	toggleMenu() {
		this.isOpen ? this.close() : this.open();
	}

	setCurrency(code) {
		this.currency = code;
		each(this.elements.amounts, (el) => {
			const val = el.dataset[code];
			if (val) el.textContent = val;
		});
		each(this.elements.codes, (el) => {
			const amount = el.previousElementSibling?.textContent;
			el.textContent = amount === 'Custom' ? '' : code.toUpperCase();
		});
		each(this.elements.options, (el) => {
			el.classList.toggle('is-active', el.dataset.currency === code);
		});
		this.elements.current.textContent = code.toUpperCase();
	}

	addEventListeners() {
		this.onToggle = (e) => {
			e.stopPropagation();
			this.toggleMenu();
		};
		this.onOptionClick = (e) => {
			const code = e.currentTarget.dataset.currency;
			if (code) this.setCurrency(code);
			this.close();
		};
		this.onDocClick = (e) => {
			if (this.isOpen && !this.elements.currency.contains(e.target)) {
				this.close();
			}
		};

		this.onPrev = () => this.scrollTo(this.currentIndex() - 1);
		this.onNext = () => this.scrollTo(this.currentIndex() + 1);
		this.onGridScroll = () => this.updateArrows();

		this.elements.toggle.addEventListener('click', this.onToggle);
		each(this.elements.options, (el) =>
			el.addEventListener('click', this.onOptionClick)
		);
		document.addEventListener('click', this.onDocClick);
		this.elements.arrowPrev?.addEventListener('click', this.onPrev);
		this.elements.arrowNext?.addEventListener('click', this.onNext);
		this.elements.grid?.addEventListener('scroll', this.onGridScroll, {
			passive: true,
		});
	}

	removeEventListeners() {
		if (this.elements?.toggle) {
			this.elements.toggle.removeEventListener('click', this.onToggle);
		}
		each(this.elements?.options, (el) =>
			el.removeEventListener('click', this.onOptionClick)
		);
		document.removeEventListener('click', this.onDocClick);
		this.elements?.arrowPrev?.removeEventListener('click', this.onPrev);
		this.elements?.arrowNext?.removeEventListener('click', this.onNext);
		this.elements?.grid?.removeEventListener('scroll', this.onGridScroll);
	}

	onResize() {
		this.updateArrows();
	}

	destroy() {
		this.removeEventListeners();
		super.destroy();
	}
}
